/**
 * ClientConfirmSection — client detail + selection header for the
 * combined-prefill pre_launch drawer (analyze → auto-open), shared by every
 * host.
 *
 * Rendered ABOVE the input form via InstanceDetailDrawer's
 * ``renderClientSection`` render-prop (mirrors ``renderInputForm``) on the
 * add-in; ora's `workflows.triage.$messageId` route (story 26752) mounts it
 * directly above `LaunchForm` the same way. Purely props-driven — the HOST
 * owns the client state, never this component; it only renders three states:
 *
 *   resolved client → the shared ClientContextTile (full client details:
 *                     name, location, branch, policies-in-force,
 *                     opportunities — R3, ui/CLAUDE.md parity with LaunchTab's
 *                     pre-launch tile) with a "Change" link on its name row,
 *                     ALWAYS, so the AM can override any pick. Launching IS
 *                     the confirmation (R2) — there is no Confirm button/gate.
 *                     Story 28633 dropped the confidence badge: a confident
 *                     (`high`) or human pick (no rating) shows NO heading at
 *                     all; only an unsure pick (`medium` / `low`) adds a
 *                     "Confirm client" heading and ONE amber line asking the
 *                     AM to check.
 *   no client + suggestions   → "did you mean" picker
 *   no client + no suggestions → ora's client search (ClientQuicklinkDropdown,
 *                                 the header's control — story 28633)
 *
 * Picking a different client (Change / did-you-mean / search) fires
 * ``onSelectClient`` — the host re-runs prefill (add-in) or re-fetches client
 * details (ora) for the newly-chosen client.
 *
 * Moved from add-in-only `src/components/ClientConfirmSection.tsx` to this
 * shared location (story 26752) when ora's triage route became its second
 * consumer — a forked copy is exactly the drift ui/CLAUDE.md bans, and the
 * two hosts rendering visibly different client cards for the same brief was
 * the bug report that prompted the move.
 */
import { useEffect, useState } from "react";
import type { ClientDetails, ClientSearchResult } from "~/common/workflows/types/client";
import { TYPE_BODY, TYPE_LABEL } from "~/common/workflows/paneTypography";
import { ClientContextTile } from "./ClientContextTile";
import { ClientSearchField } from "./ClientSearchField";
import { ClientSuggestionRow } from "./ClientSuggestionRow";

export interface ClientConfirmSectionProps {
  /** The resolved client (full details), or null when analyze couldn't resolve one. */
  details: ClientDetails | null;
  /** Substrate's self-rated pick certainty; absent on human-picked clients. */
  pickConfidence?: "high" | "medium" | "low";
  /** "Did you mean" candidates from analyze_email (no-client state). */
  suggestions: ClientSearchResult[];
  /** True while the client lookup is still resolving. */
  loading: boolean;
  /** Fires with the FULL picked result (not just clientSk) so the caller can
   *  seed clientName immediately instead of waiting on getClientDetails. */
  onSelectClient: (result: ClientSearchResult) => void;
  /** Async search for the manual-picker state (searchClientsLegacy wrap). */
  searchClients?: (q: string) => Promise<ClientSearchResult[]>;
}

/** The one line an unsure pick shows (story 28633). */
export const CLIENT_UNSURE_COPY = "ora isn't sure this is the right client. Please check.";

export function ClientConfirmSection({
  details,
  pickConfidence,
  suggestions,
  loading,
  onSelectClient,
  searchClients,
}: ClientConfirmSectionProps) {
  // "Change" toggles the picker open under the client details. Reset whenever a
  // different client resolves (a pick here or the async selectClient landing).
  const [changing, setChanging] = useState(false);
  useEffect(() => {
    setChanging(false);
  }, [details?.client.client_sk]);

  const handlePick = (result: ClientSearchResult) => {
    setChanging(false);
    onSelectClient(result);
  };

  // ora's ONE client search control (story 28633). Its row shows city/state/
  // zip, location and client type, so same-named clients are told apart.
  // `autoFocus` only when the AM just asked for it (Change) — never on load.
  const picker = (heading: string, autoFocus = false) => (
    <div className="flex flex-col gap-2">
      {suggestions.length > 0 && (
        <div data-testid="client-suggestions">
          <div className="mb-1 text-xs text-primary-gray-light-400">
            {heading}
          </div>
          <div className="overflow-hidden rounded-md border border-primary-gray-light-200">
            {/* Name, then city/state and branch (story 28633) — two clients
                with the same name no longer read as one listed twice. */}
            {suggestions.map((r, i) => (
              <ClientSuggestionRow
                key={r.clientSk}
                result={r}
                onPick={handlePick}
                last={i === suggestions.length - 1}
              />
            ))}
          </div>
        </div>
      )}
      {suggestions.length === 0 && (
        <div className="text-xs text-primary-gray-light-400">{heading}</div>
      )}
      {searchClients && (
        <ClientSearchField
          id="client-confirm-search"
          searchClients={searchClients}
          onPick={handlePick}
          autoFocus={autoFocus}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2"
        data-testid="client-confirm-section"
        role="status"
      >
        <span
          className="inline-block h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-primary-gray-light-300 border-t-primary-gray-light-700"
          aria-hidden="true"
        />
        <p className="min-w-0 text-xs text-primary-gray-light-700">
          Finding the client from this email…
        </p>
        <span className="sr-only">
          Identifying client
        </span>
      </div>
    );
  }

  if (details) {
    // Only a pick ora itself rated below "high" asks the AM to look. A
    // confident pick and a human pick (no rating) just show the client.
    const unsure = pickConfidence === "medium" || pickConfidence === "low";
    return (
      <div className="flex flex-col gap-1.5" data-testid="client-confirm-section">
        {unsure && (
          <div className="flex flex-col gap-1" data-testid="client-confirm-unsure">
            <h3 className={`${TYPE_LABEL} text-primary-gray-light-600`}>
              Confirm client
            </h3>
            <p
              className={`${TYPE_BODY} rounded-md border border-primary-warning-200 bg-primary-warning-50 px-2 py-1 text-primary-warning-700`}
              data-testid="client-confirm-unsure-note"
            >
              {CLIENT_UNSURE_COPY}
            </p>
          </div>
        )}
        {/* Full client details — reuse the shared ClientContextTile that
            LaunchTab's pre-launch client tile renders (ui/CLAUDE.md parity):
            name, location, branch, policies-in-force, opportunities. Change
            sits on its name row so it costs no row of its own. */}
        <ClientContextTile
          kind="loaded"
          details={details}
          collapsiblePolicies
          flush
          headerAction={
            <button
              type="button"
              onClick={() => setChanging((c) => !c)}
              data-testid="client-change-button"
              className={`ml-2 inline-flex flex-shrink-0 touch-target items-center ${TYPE_BODY} text-primary-gray-light-500 underline hover:text-primary-gray-light-700`}
            >
              {changing ? "Cancel" : "Change"}
            </button>
          }
        />
        {changing && picker("Select a different client:", true)}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-primary-gray-light-200 bg-primary-gray-light-25 px-3 py-2"
      data-testid="client-confirm-section"
    >
      {picker(
        suggestions.length > 0
          ? "Could not identify client — did you mean:"
          : "Search for the client this workflow is for:",
      )}
    </div>
  );
}

export default ClientConfirmSection;
